import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'
import { useNavigate, useParams } from 'react-router-dom'
import {
  adminService,
  type FormResponsesResult,
} from '../../services/adminService'
import { LoadingState, EmptyState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  if (Array.isArray(v)) return v.join(', ')
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return String(v)
}

function formatDateTime(iso?: string): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function AdminResponsesPage() {
  const { id = '', formId = '' } = useParams()
  const navigate = useNavigate()
  const { notify } = useToast()
  const [data, setData] = useState<FormResponsesResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    adminService
      .listResponses(id, formId)
      .then(setData)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))
  }, [id, formId, notify])

  const handleExport = async () => {
    setDownloading(true)
    try {
      const blob = await adminService.exportResponses(id, formId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const title = data?.form.title ?? 'responses'
      a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}-responses.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setDownloading(false)
    }
  }

  if (loading) return <LoadingState />

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => navigate(`/admin/events/${id}`)}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Back to event
      </Button>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            {data?.form.title ?? 'Responses'}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
            <Chip
              label={`${data?.total ?? 0} response${data?.total === 1 ? '' : 's'}`}
              size="small"
              sx={{ bgcolor: 'rgba(0,117,188,0.1)', color: 'primary.main', fontWeight: 600 }}
            />
          </Stack>
        </Box>
        <Button
          variant="contained"
          startIcon={<DownloadRoundedIcon />}
          onClick={handleExport}
          disabled={downloading || (data?.total ?? 0) === 0}
        >
          {downloading ? 'Exporting…' : 'Export CSV'}
        </Button>
      </Stack>

      {!data || data.total === 0 ? (
        <EmptyState
          icon={<AssignmentTurnedInRoundedIcon fontSize="large" />}
          title="No responses yet"
          description="Responses will appear here as attendees submit this form."
        />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Submitted</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Attendee</TableCell>
                {data.fields.map((f) => (
                  <TableCell key={f.id} sx={{ fontWeight: 700 }}>
                    {f.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.responses.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(r.submittedAt)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.attendeeEmail || '—'}
                  </TableCell>
                  {data.fields.map((f) => (
                    <TableCell key={f.id}>
                      {formatValue(r.values[f.id] ?? r.values[f.label])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  )
}
